import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Send, Heart, CheckCircle, AlertCircle } from 'lucide-react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { SEO } from '../components/SEO';
import { fadeInUp, fadeInLeft, fadeInRight, viewportSettings } from '../hooks/useScrollAnimation';

const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbyDA6Y15clodKDKBAkbuayUxF5CLA_rxAQK1SiktHdUaQfeK8Z1q85NKIc6XifWFITr/exec";
const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const SupporterForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    message: '',
    email_opt_in: false,
    sms_opt_in: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Safety check for recaptcha
    if (!executeRecaptcha) {
      console.log('Recaptcha not ready');
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Execute Recaptcha (Optional: You can pass this token to Google Script if updated to verify it)
      const token = executeRecaptcha ? await executeRecaptcha('supporter_signup') : null;
      
      // Prepare payload exactly as the Google Script expects
      // Note: We are now submitting DIRECTLY to Google Sheets from the browser (Serverless)
      // This ensures the form works on static hosting (like GoDaddy Shared Hosting)
      
      const val_email = formData.email_opt_in ? "Yes" : "No";
      const val_sms = formData.sms_opt_in ? "Yes" : "No";

      const payload = {
        "firstName": formData.first_name,
        "lastName": formData.last_name,
        "email": formData.email,
        "phone": formData.phone,
        "address": formData.address,
        "message": formData.message,
        "smsOptIn": val_sms,
        "emailOptIn": val_email,
        "SMS Opt-In": val_sms,
        "Email Opt-In": val_email,
        "recaptcha_token": token
      };

      // Use fetch with no-cors mode to bypass CORS restrictions on Google Scripts
      // Note: We won't get a readable JSON response in no-cors mode, but the script will execute.
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      // Assume success if no network error occurred
      setSubmitStatus('success');
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        message: '',
        email_opt_in: false,
        sms_opt_in: false
      });
    } catch (error) {
      console.error("Submission Error:", error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [executeRecaptcha, formData]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="supporter-form">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="first_name" className="text-white uppercase tracking-wider text-sm">
            First Name *
          </Label>
          <Input
            id="first_name"
            name="first_name"
            type="text"
            required
            value={formData.first_name}
            onChange={handleChange}
            placeholder="John"
            data-testid="input-first-name"
            className="bg-[#0A0A0A] border-[#DAA520]/30 text-white placeholder:text-gray-600 focus:border-[#DAA520] h-12"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name" className="text-white uppercase tracking-wider text-sm">
            Last Name *
          </Label>
          <Input
            id="last_name"
            name="last_name"
            type="text"
            required
            value={formData.last_name}
            onChange={handleChange}
            placeholder="Doe"
            data-testid="input-last-name"
            className="bg-[#0A0A0A] border-[#DAA520]/30 text-white placeholder:text-gray-600 focus:border-[#DAA520] h-12"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-white uppercase tracking-wider text-sm">
          Email Address *
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          required
          value={formData.email}
          onChange={handleChange}
          placeholder="john.doe@example.com"
          data-testid="input-email"
          className="bg-[#0A0A0A] border-[#DAA520]/30 text-white placeholder:text-gray-600 focus:border-[#DAA520] h-12"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="text-white uppercase tracking-wider text-sm">
          Phone Number *
        </Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          required
          value={formData.phone}
          onChange={handleChange}
          placeholder="(555) 123-4567"
          data-testid="input-phone"
          className="bg-[#0A0A0A] border-[#DAA520]/30 text-white placeholder:text-gray-600 focus:border-[#DAA520] h-12"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="address" className="text-white uppercase tracking-wider text-sm">
          Address
        </Label>
        <Input
          id="address"
          name="address"
          type="text"
          value={formData.address}
          onChange={handleChange}
          placeholder="123 Main St, Benton, KY"
          data-testid="input-address"
          className="bg-[#0A0A0A] border-[#DAA520]/30 text-white placeholder:text-gray-600 focus:border-[#DAA520] h-12"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-white uppercase tracking-wider text-sm">
          Message (Optional)
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us why you support Barry Howell..."
          data-testid="input-message"
          className="bg-[#0A0A0A] border-[#DAA520]/30 text-white placeholder:text-gray-600 focus:border-[#DAA520] min-h-[120px] resize-none"
        />
      </div>

      {/* Communication Preferences */}
      <div className="space-y-4 pt-2">
        <p className="text-white uppercase tracking-wider text-sm font-medium">
          Stay Connected
        </p>
        
        <div className="flex items-start space-x-3">
          <Checkbox
            id="email_opt_in"
            checked={formData.email_opt_in}
            onCheckedChange={(checked) => handleCheckboxChange('email_opt_in', checked)}
            data-testid="checkbox-email-opt-in"
            className="border-[#DAA520]/50 data-[state=checked]:bg-[#DAA520] data-[state=checked]:border-[#DAA520] mt-0.5"
          />
          <Label 
            htmlFor="email_opt_in" 
            className="text-gray-300 text-sm leading-relaxed cursor-pointer"
          >
            Yes, I'd like to receive email updates about the campaign, upcoming events, and ways to get involved.
          </Label>
        </div>
        
        <div className="flex items-start space-x-3">
          <Checkbox
            id="sms_opt_in"
            checked={formData.sms_opt_in}
            onCheckedChange={(checked) => handleCheckboxChange('sms_opt_in', checked)}
            data-testid="checkbox-sms-opt-in"
            className="border-[#DAA520]/50 data-[state=checked]:bg-[#DAA520] data-[state=checked]:border-[#DAA520] mt-0.5"
          />
          <Label 
            htmlFor="sms_opt_in" 
            className="text-gray-300 text-sm leading-relaxed cursor-pointer"
          >
            Yes, I consent to receive text message updates from the Barry Howell campaign. Message and data rates may apply. Reply STOP to opt out.
          </Label>
        </div>
      </div>

      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className="flex items-center gap-3 p-4 bg-green-900/20 border border-green-500/30 rounded-sm" data-testid="success-message">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span className="text-green-400">Thank you for signing up! We'll keep you updated on the campaign.</span>
        </div>
      )}

      {submitStatus === 'duplicate' && (
        <div className="flex items-center gap-3 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-sm" data-testid="duplicate-message">
          <AlertCircle className="w-5 h-5 text-yellow-500" />
          <span className="text-yellow-400">This email is already registered. Thank you for your continued support!</span>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="flex items-center gap-3 p-4 bg-red-900/20 border border-red-500/30 rounded-sm" data-testid="error-message">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-400">Something went wrong. Please try again later.</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        data-testid="submit-supporter-btn"
        className="w-full bg-[#DAA520] text-black hover:bg-[#E6B422] font-bold uppercase tracking-wider py-6 text-lg gold-glow disabled:opacity-50"
      >
        {isSubmitting ? (
          'Submitting...'
        ) : (
          <>
            Sign Up
            <Send className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </form>
  );
};

export const StayInformed = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-16 sm:pt-20">
      <SEO 
        title="Join the Campaign | Barry Howell for Marshall County Jailer"
        description="Sign up to support Barry Howell for Marshall County Jailer. Volunteer, donate, or get updates on the election in Marshall County, KY."
        keywords="Barry Howell volunteer, donate Marshall County Jailer, campaign sign up, election updates, get involved"
        canonical="https://mcjailer.com/stay-informed"
      />
      
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_jailer-campaign/artifacts/8tidws91_DSC09773.jpg"
            alt="Background"
            className="w-full h-full object-cover object-top brightness-[0.35] contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/40 to-[#0A0A0A]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="text-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <p className="text-[#DAA520] text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">
              Join the Campaign
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white uppercase tracking-tighter font-['Barlow_Condensed']">
              Stay Informed
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mt-4 sm:mt-6 max-w-2xl mx-auto px-4">
              Sign up to receive campaign updates and learn how you can help Barry Howell become the next Marshall County Jailer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-10 sm:py-16 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            {/* Signup Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewportSettings}
              variants={fadeInLeft}
            >
              <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
                  Become a Supporter
                </h2>
                <p className="text-gray-400">
                  Fill out the form below to join our growing list of supporters and stay updated on the campaign.
                </p>
              </div>

              <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_KEY}>
                <SupporterForm />
              </GoogleReCaptchaProvider>
            </motion.div>

            {/* Donation Section */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewportSettings}
              variants={fadeInRight}
            >
              <div className="lg:sticky lg:top-28">
                <div className="bg-[#0A0A0A] border border-[#DAA520]/30 p-6 sm:p-8 md:p-12">
                  <div className="text-center">
                    <Heart className="w-16 h-16 text-[#DAA520] mx-auto mb-6" />
                    <h2 className="text-3xl md:text-4xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
                      Donate to the Campaign
                    </h2>
                    <p className="text-gray-400 mb-8 leading-relaxed">
                      Your generous contribution helps us spread Barry's message across Marshall County. Every dollar makes a difference in building a safer community.
                    </p>

                    {/* PayPal Donate Button */}
                    <Button
                      onClick={() => window.open('https://www.paypal.com/donate/?hosted_button_id=YMDPJ3HQR2LNL', '_blank')}
                      className="w-full bg-[#DAA520] text-black hover:bg-[#E6B422] font-bold uppercase tracking-wider py-6 text-lg mb-4"
                    >
                      Donate Now
                    </Button>
                  </div>

                  {/* Donation Info */}
                  <div className="mt-12 pt-8 border-t border-[#DAA520]/20">
                    <h3 className="text-lg font-bold text-white uppercase tracking-wider font-['Barlow_Condensed'] mb-4">
                      Other Ways to Help
                    </h3>
                    <ul className="space-y-3 text-gray-400 text-sm">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-[#DAA520] flex-shrink-0 mt-0.5" />
                        <span>Share campaign posts on social media</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-[#DAA520] flex-shrink-0 mt-0.5" />
                        <span>Display a yard sign in your community</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-[#DAA520] flex-shrink-0 mt-0.5" />
                        <span>Talk to friends and family about Barry</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-[#DAA520] flex-shrink-0 mt-0.5" />
                        <span>Volunteer for campaign events</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Campaign Disclaimer */}
                <p className="text-center text-gray-600 text-xs mt-6 px-4">
                  Paid for by Barry Howell for Marshall County Jailer. Contributions are not tax deductible.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
